import React, { useEffect, useState, useCallback } from "react";
import OrderDetails from "./OrderDetails";
import Modal from "./Modal";
import { adminApiRequest } from "../utils/adminApi";

export default function DeletedOrders() {
  const [orders, setOrders] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [restoreModalOrder, setRestoreModalOrder] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const fetchDeletedOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApiRequest({ script: "get-deleted-orders" });
      setOrders(data?.orders || []);
    } catch (error) {
      console.error("Failed to fetch deleted orders:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeletedOrders();
  }, [fetchDeletedOrders]);

  const toggleOrder = (id) => {
    setExpandedOrderId((prev) => (prev === id ? null : id));
  };

  const openRestoreModal = (order, e) => {
    e.stopPropagation();
    setRestoreModalOrder(order);
  };

  const closeRestoreModal = () => {
    if (restoring) return;
    setRestoreModalOrder(null);
  };

  const handleRestoreOrder = async (e) => {
    e.preventDefault();
    if (!restoreModalOrder || restoring) return;

    setRestoring(true);

    try {
      await adminApiRequest({
        script: "restore-order",
        data: { order_id: restoreModalOrder.id },
      });

      await fetchDeletedOrders();
      closeRestoreModal();
    } catch (err) {
      console.error("Restore failed:", err);

      if (err.message === "REQUEST_TIMEOUT") {
        alert("Request timed out. Please try again.");
      } else if (err.message === "UNAUTHORIZED") {
        alert("Session expired. Please log in again.");
      } else {
        alert("Failed to restore order.");
      }
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-6 p-2">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Cancelled Orders
          </h1>
        </div>

        {loading && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/20 backdrop-blur-sm">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-300 border-t-slate-900" />
          </div>
        )}

        {!loading && orders.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center shadow-sm">
            <p className="text-sm font-medium text-slate-400">
              No cancelled orders found.
            </p>
          </div>
        ) : (
          <ul className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {orders.map((order) => {
              const expanded = expandedOrderId === order.id;

              return (
                <li
                  key={order.id}
                  onClick={() => toggleOrder(order.id)}
                  className={`h-full cursor-pointer overflow-hidden rounded-2xl border bg-white shadow-sm transition ${
                    expanded
                      ? "border-red-200 ring-1 ring-red-100"
                      : "border-slate-200 hover:border-slate-300 hover:shadow-md"
                  }`}
                >
                  <div className="h-1 w-full bg-red-500" />

                  <div className="p-5">
                    <div className="mb-4 flex items-start justify-between gap-4">
                      <div className="flex flex-wrap items-center gap-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                        <span className="rounded-full bg-slate-100 px-3 py-1">
                          {new Date(order.created_at).toLocaleDateString("en-GB")}
                        </span>
                        <span className="rounded-full bg-slate-100 px-3 py-1">
                          ID: {order.id}
                        </span>
                        <span className="rounded-full bg-red-50 px-3 py-1 text-red-600">
                          Cancelled
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => openRestoreModal(order, e)}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm transition hover:bg-emerald-700 active:scale-95"
                        title="Restore Order"
                      >
                        <i className="fa-solid fa-rotate-left text-sm" />
                      </button>
                    </div>

                    <div className="grid gap-4">
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                          <p className="text-lg font-semibold text-slate-900">
                            Order #{order.order_number}
                          </p>

                          <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                            {order.order_type}
                          </span>

                          {order.order_type === "Eat In" && order.table_number && (
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                              Table {order.table_number}
                            </span>
                          )}
                        </div>

                        <div className="space-y-2 text-md text-slate-600">
                          <p>
                            <span className="font-semibold text-slate-800">
                              Customer:
                            </span>{" "}
                            {order.customer_name || "Unnamed Customer"}
                          </p>

                          <p className="text-xl font-bold tracking-tight text-slate-900">
                            £{parseFloat(order.total_price).toFixed(2)}
                          </p>
                        </div>
                      </div>

                      
                    </div>
                  </div>

                  {expanded && (
                    <div className="border-t border-slate-100 bg-slate-50 px-5 py-5">
                      <OrderDetails order={order} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {restoreModalOrder && (
          <Modal
            title="Restore Order"
            onClose={closeRestoreModal}
            onSubmit={handleRestoreOrder}
            showCancel={true}
            showSubmitButton={true}
            submitButtonClass="bg-emerald-600 hover:bg-emerald-700 text-white"
            submitButtonText={
              restoring ? (
                <span className="inline-flex items-center gap-2">
                  <i className="fas fa-spinner fa-spin" />
                  Restoring...
                </span>
              ) : (
                "Restore Order"
              )
            }
            className="max-w-md"
          >
            <div className="space-y-3 text-sm text-slate-600">
              <p>
                Restore <strong>Order #{restoreModalOrder.order_number}</strong>{" "}
                back to <strong>To-Do</strong>?
              </p>
              <p>This order will reappear on the active orders board.</p>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}